import React from 'react';
import {Controls, ControlsItem, PageContent, Preloader, Switch, Typography} from '@genestack/ui';
import classNames from 'classnames';
import {Sidebar} from '../../../../../components/layout/sidebar';
import {SidebarHeader} from '../../../../../components/layout/sidebar/sidebar-header/sidebar-header';
import {useTreeStructureQuery} from './hooks/use-tree-structure-query';
import {getRemainingRootsNum, getTreeItem} from './helpers';
import {isDocumentBeingEdited} from '../../../../../utils/document';
import {useFoldingItemsState} from '../../../../../hooks/use-folding-items-state';
import styles from './ontology-tree-item.module.css';
import {LS_PREFIX} from '../../../../../providers/global-state';
import {useLsFlag} from '../../../../../hooks/use-ls-flag';
import {OntologyTreeInitialItem} from './ontology-tree-initial-item';
import {LoadMoreControl} from '../../../../../components/pagination/load-more-control/load-more-control';
import {useAdditionalRootsQuery} from './hooks/use-additional-roots-query';
import {extractInfiniteQueryItems} from '../../../../../components/pagination/infinite-query-items';
import {OntologyTreeItem} from './ontology-tree-item';
import {AdditionalElementsPageResponse} from './interface';

interface Props {
    onClose: () => void;
    docInfo: DocInfo;
    foldingItemsProps: ReturnType<typeof useFoldingItemsState>;
    focusNode: (nodeId: string) => void;
}

export function OntologyTreeSidebar(props: Props) {
    const isEditedDoc = isDocumentBeingEdited(props.docInfo);
    const [isCurrentDocOnly, setIsCurrentDocOnly] = useLsFlag(
        `${LS_PREFIX}.document.edit.${props.docInfo.accession}.isCurrentDocOnly`,
        true
    );
    const treeStructureQuery = useTreeStructureQuery(
        props.docInfo.accession,
        Boolean(isCurrentDocOnly),
        isEditedDoc
    );

    const additionalRootsQuery = useAdditionalRootsQuery({
        accession: props.docInfo.accession,
        cursor: treeStructureQuery.data?.rootIdsPage.nextCursor || null
    });

    const additionalRoots = extractInfiniteQueryItems(
        additionalRootsQuery,
        (response: AdditionalElementsPageResponse) => response.data
    );

    const initialRootIds = treeStructureQuery.data?.rootIdsPage.data;

    const isMultiDocSupported = !props.docInfo.readOnly;
    return (
        <Sidebar border="left">
            <SidebarHeader title="Hierarchical tree" onClose={props.onClose}>
                {isMultiDocSupported && (
                    <Typography
                        as="label"
                        variant="section"
                        intent="quiet"
                        className={styles.onlyThisDocSwitch}
                    >
                        <Controls>
                            <ControlsItem>
                                <Switch
                                    checked={isCurrentDocOnly}
                                    onCheckedChange={setIsCurrentDocOnly}
                                />
                            </ControlsItem>
                            <ControlsItem>Current document only</ControlsItem>{' '}
                        </Controls>
                    </Typography>
                )}
            </SidebarHeader>

            {(function () {
                if (!treeStructureQuery.data) {
                    return (
                        <PageContent>
                            <Preloader show count={5} box="paragraph" />
                        </PageContent>
                    );
                }

                const remaining = getRemainingRootsNum(
                    treeStructureQuery.data.rootIdsPage,
                    additionalRootsQuery.data?.pages
                );

                return (
                    <div>
                        {initialRootIds!.map((id) => {
                            const element = getTreeItem(treeStructureQuery.data, id);

                            return (
                                <OntologyTreeInitialItem
                                    key={`ontology-item:${JSON.stringify(id)}`}
                                    id={id}
                                    ontologyTreeData={treeStructureQuery.data}
                                    foldingItemsProps={props.foldingItemsProps}
                                    padding={1}
                                    currentDocInfo={props.docInfo}
                                    element={element}
                                    isCurrentDocOnly={isCurrentDocOnly}
                                    focusNode={props.focusNode}
                                />
                            );
                        })}

                        {additionalRoots &&
                            additionalRoots.map((additionalChild) => (
                                <OntologyTreeItem
                                    id={additionalChild.element.id}
                                    ontologyTreeData={treeStructureQuery.data}
                                    key={`ontology-item:${JSON.stringify(additionalChild.element.id)}`}
                                    padding={1}
                                    foldingItemsProps={props.foldingItemsProps}
                                    currentDocInfo={props.docInfo}
                                    element={additionalChild.element}
                                    additionalChildrenNum={additionalChild.childrenCount}
                                    initialChildren={undefined}
                                    isAdditionalElement
                                    isCurrentDocOnly={isCurrentDocOnly}
                                    focusNode={props.focusNode}
                                />
                            ))}

                        {remaining > 0 && (
                            <LoadMoreControl
                                onClick={additionalRootsQuery.fetchNextPage}
                                className={classNames(styles.ontologyTreeItem, styles.withButton)}
                                isLoading={additionalRootsQuery.isFetching}
                                // @ts-ignore
                                style={{'--tree-item-padding': 2}}
                            >
                                ...Load next elements
                            </LoadMoreControl>
                        )}
                    </div>
                );
            })()}
        </Sidebar>
    );
}
